import { createSelector } from 'reselect';
import { isEqual } from 'lodash';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { FINANCE, LEASE, CASH } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import {
    selectEffectiveAmountFinanced,
    selectEffectiveDownPayment,
    selectEffectiveRetailPrice,
    getEffectiveSellingPrice
} from './dealEffectiveSelectors';
import {
    getNetSellingPrice,
    getTotalAddons,
    getIncludedCapLenderFeesTotal,
    getAddsToCapCostTotal as getComputedAddsToCapCostTotal
} from './compositeSelectors';
import { getTotalNetTradeValueForIncludedActiveTradeIns } from './tradeIns';
import {
    getCurrentOfferType,
    getAppliedIncentivesTotalForDisplay,
    getAppliedIncentivesTotalForNonDealerCash,
    getTotalTax,
    isCalculatingPayment,
    getBaseMonthlyPayment,
    getMonthlyUseTax,
    getBaseMonthlyPaymentTotal,
    getSelectedTerm,
    getMonthlyRentCharged,
    getDiaDueAtSigning,
    getDiaLenderFees,
    getDiaGovFees,
    getDiaDealerFees,
    getDiaAccessories,
    getDiaProtectionProducts,
    getCapCostReduction,
    getFirstPaymentCapitalized,
    getTotalDepreciation,
    getAdjustedResidualValue,
    getAdjustedCapCost,
    getCapCostTotal,
    getNetCashDown,
    getCapReductionRebate,
    getCapReductionTradeIn,
    getAddsToCapCostTotal,
    isOnePayLease,
    getUpfrontTaxTotal,
    getFirstPaymentAmount,
    getCashTotalAmountDueAfterCredits
} from './offerRedux';
import {
    getPrimaryTaxTotal,
    getAdditionalTaxTotal,
    getAdditionalTaxBreakdown,
    getDealerFeeTotal,
    getDmvFeeTotal,
    getItemizedDealerFees,
    getItemizedGovernmentFees,
    getMonthlyPayment,
    getSecurityDepositOverrideWithFallback
} from './offerSelectors';
import { getPrimaryTaxBreakdown } from './offerRedux';
import * as calculationDetailsSelectors from './calculationDetailsSelectors';
import { getDasCapCostReduction } from './dueAtSigningSelectors';
import { getMsrpDiscount } from './dealerDiscountsSelectors';
import { getAccessoriesTotalAmount } from './accessoriesSelectors';
import { getVppSummary, getVppSelectedProducts } from './vehicleProtectionSelectors';

// Half a cent: a variance below this rounds to $0.00 on screen. Exact equality (===) on money would flag
// valid deals because chained float subtraction, e.g. in checkAmtFinanced, leaves noise around 1e-12.
const TOLERANCE = 0.005;

export type InvariantResult = {
    identity: string;
    stated: number;
    implied: number;
    variance: number;
    inputs: Record<string, number>;
    passed: boolean;
};

const pickRebates = (state: StateTree): number => {
    const useDisplayTotal = featureToggleSelectors.isProgramIncentivesRestorationEnabled(state as any);
    return useDisplayTotal
        ? getAppliedIncentivesTotalForDisplay(state as any) ?? 0
        : getAppliedIncentivesTotalForNonDealerCash(state as any) ?? 0;
};

export const checkSellingPrice = (state: StateTree): InvariantResult => {
    const stated = getEffectiveSellingPrice(state, undefined) ?? 0;
    const msrp = selectEffectiveRetailPrice(state, undefined) ?? 0;
    const discounts = getMsrpDiscount(state, undefined) ?? 0;
    const implied = msrp - discounts;
    const variance = implied - stated;
    return {
        identity: 'finance.sellingPrice',
        stated,
        implied,
        variance,
        inputs: { msrp, discounts },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkProtectionTotal = (state: StateTree): InvariantResult => {
    const stated = getVppSummary(state).totalProductPrice ?? 0;
    const products = getVppSelectedProducts(state);
    const implied = products.reduce((sum: number, p: any) => sum + (p.productPrice ?? 0), 0);
    const variance = implied - stated;
    return {
        identity: 'finance.protectionTotal',
        stated,
        implied,
        variance,
        inputs: { productCount: products.length },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkDealerFeesTotal = (state: StateTree): InvariantResult => {
    const stated = getDealerFeeTotal(state as any) ?? 0;
    const items = getItemizedDealerFees(state).filter((f: any) => f.includedInCalc || f.isFeeIncludedInCalculation);
    const implied = items.reduce((sum: number, f: any) => sum + (f.dealerFeeAmount ?? 0), 0);
    const variance = implied - stated;
    return {
        identity: 'finance.dealerFeesTotal',
        stated,
        implied,
        variance,
        inputs: { itemCount: items.length },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkGovFeesTotal = (state: StateTree): InvariantResult => {
    const stated = getDmvFeeTotal(state as any) ?? 0;
    const items = getItemizedGovernmentFees(state);
    const implied = items.reduce((sum: number, f: any) => sum + (f.amount ?? 0), 0);
    const variance = implied - stated;
    return {
        identity: 'finance.govFeesTotal',
        stated,
        implied,
        variance,
        inputs: { itemCount: items.length },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkAmtFinanced = (state: StateTree): InvariantResult => {
    const stated = selectEffectiveAmountFinanced(state, undefined) ?? 0;
    const netSP = getNetSellingPrice(state) ?? 0;
    const netTrades = getTotalNetTradeValueForIncludedActiveTradeIns(state) ?? 0;
    const rebates = pickRebates(state);
    const cashDown = selectEffectiveDownPayment(state, undefined) ?? 0;
    const implied = netSP - netTrades - rebates - cashDown;
    const variance = implied - stated;

    return {
        identity: 'finance.amtFinanced',
        stated,
        implied,
        variance,
        inputs: { netSellingPrice: netSP, netTrades, rebates, cashDown },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkTotalTax = (state: StateTree): InvariantResult => {
    const stated = getTotalTax(state as any) ?? 0;
    const primary = getPrimaryTaxTotal(state as any) ?? 0;
    const additional = getAdditionalTaxTotal(state as any) ?? 0;
    const implied = primary + additional;
    const variance = implied - stated;
    return {
        identity: 'finance.totalTax',
        stated,
        implied,
        variance,
        inputs: { primaryTax: primary, additionalTax: additional },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLenderFeesExcludedFromAddons = (state: StateTree): InvariantResult => {
    const stated = getTotalAddons(state) ?? 0;
    const accessories = getAccessoriesTotalAmount(state) ?? 0;
    const protection = getVppSummary(state).totalProductPrice ?? 0;
    const dealerFees = getDealerFeeTotal(state) ?? 0;
    const govFees = getDmvFeeTotal(state) ?? 0;
    const lenderFees = getIncludedCapLenderFeesTotal(state) ?? 0;
    const implied = accessories + protection + dealerFees + govFees + lenderFees;
    const variance = implied - stated;
    return {
        identity: 'finance.lenderFeesExcludedFromAddons',
        stated,
        implied,
        variance,
        inputs: { accessories, protection, dealerFees, govFees, lenderFees },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkAdditionalTaxTotal = (state: StateTree): InvariantResult => {
    const stated = getAdditionalTaxTotal(state as any) ?? 0;
    const raw = getAdditionalTaxBreakdown(state as any) ?? {};
    const breakdown = Object.fromEntries(Object.entries(raw).filter(([k]) => !k.toLowerCase().includes('rate')));
    const implied = Object.values(breakdown).reduce((sum: number, v) => sum + (v as number), 0);
    const variance = implied - stated;
    return {
        identity: 'finance.additionalTaxTotal',
        stated,
        implied,
        variance,
        inputs: { regionCount: Object.keys(breakdown).length },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkPrimaryTaxTotal = (state: StateTree): InvariantResult => {
    const stated = getPrimaryTaxTotal(state as any) ?? 0;
    const breakdown = getPrimaryTaxBreakdown(state as any) ?? {};
    const implied = Object.values(breakdown).reduce((sum: number, v) => sum + (v as number), 0);
    const variance = implied - stated;
    return {
        identity: 'finance.primaryTaxTotal',
        stated,
        implied,
        variance,
        inputs: { regionCount: Object.keys(breakdown).length },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const runFinanceInvariants = (state: StateTree): InvariantResult[] => {
    if (getCurrentOfferType(state as any) !== FINANCE) return [];
    return [
        checkAmtFinanced(state),
        checkTotalTax(state),
        checkPrimaryTaxTotal(state),
        checkAdditionalTaxTotal(state),
        checkSellingPrice(state),
        checkLenderFeesExcludedFromAddons(state),
        checkProtectionTotal(state),
        checkDealerFeesTotal(state),
        checkGovFeesTotal(state)
    ];
};

export const checkLeaseAddsToCapCost = (state: StateTree): InvariantResult => {
    const stated = getAddsToCapCostTotal(state as any) ?? 0;
    const cappedTotal = getComputedAddsToCapCostTotal(state) ?? 0;
    const paymentCapitalized =
        getFirstPaymentCapitalized(state as any) ?? calculationDetailsSelectors.getIsFirstPaymentCapitalized(state as any);
    const monthly = paymentCapitalized ? getMonthlyPayment(state) ?? 0 : 0;
    const implied = cappedTotal + monthly;
    const variance = implied - stated;
    return {
        identity: 'lease.addsToCapCost',
        stated,
        implied,
        variance,
        inputs: { cappedTotal, monthly },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseGrossCapCost = (state: StateTree): InvariantResult => {
    const stated = getCapCostTotal(state as any) ?? 0;
    const selling = getEffectiveSellingPrice(state, undefined) ?? 0;
    const addsToCap = getAddsToCapCostTotal(state as any) ?? 0;
    const implied = selling + addsToCap;
    const variance = implied - stated;
    return {
        identity: 'lease.grossCapCost',
        stated,
        implied,
        variance,
        inputs: { sellingPrice: selling, addsToCapCost: addsToCap },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseCapCostReduction = (state: StateTree): InvariantResult => {
    const stated = getCapCostReduction(state, LEASE) ?? 0;
    const netCashDown = getNetCashDown(state as any) ?? 0;
    const capReductionRebate = getCapReductionRebate(state as any) ?? 0;
    const capReductionTradeIn = getCapReductionTradeIn(state as any) ?? 0;
    const implied = netCashDown + capReductionRebate + capReductionTradeIn;
    const variance = implied - stated;
    return {
        identity: 'lease.capCostReduction',
        stated,
        implied,
        variance,
        inputs: { netCashDown, capReductionRebate, capReductionTradeIn },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseAdjustedCapCost = (state: StateTree): InvariantResult => {
    const stated = getAdjustedCapCost(state as any) ?? 0;
    const grossCapCost = getCapCostTotal(state as any) ?? 0;
    const capCostReduction = getCapCostReduction(state, LEASE) ?? 0;
    const implied = grossCapCost - capCostReduction;
    const variance = implied - stated;
    return {
        identity: 'lease.adjustedCapCost',
        stated,
        implied,
        variance,
        inputs: { grossCapCost, capCostReduction },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseTotalDepreciation = (state: StateTree): InvariantResult => {
    const stated = getTotalDepreciation(state as any) ?? 0;
    const adjustedCapCost = getAdjustedCapCost(state as any) ?? 0;
    const adjustedResidualValue = getAdjustedResidualValue(state as any) ?? 0;
    const implied = adjustedCapCost - adjustedResidualValue;
    const variance = implied - stated;
    return {
        identity: 'lease.totalDepreciation',
        stated,
        implied,
        variance,
        inputs: { adjustedCapCost, adjustedResidualValue },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseBaseMonthlyPayment = (state: StateTree): InvariantResult => {
    const stated = getBaseMonthlyPayment(state as any) ?? 0;
    const term = getSelectedTerm(state as any) as any;
    const months = term?.months ?? 1;
    const totalDepreciation = getTotalDepreciation(state as any) ?? 0;
    const monthlyRentCharged = getMonthlyRentCharged(state as any) ?? 0;
    const implied = totalDepreciation / months + monthlyRentCharged;
    const variance = implied - stated;
    return {
        identity: 'lease.baseMonthlyPayment',
        stated,
        implied,
        variance,
        inputs: { totalDepreciation, months, monthlyRentCharged },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseMonthlyPayment = (state: StateTree): InvariantResult => {
    const stated = getMonthlyPayment(state) ?? 0;
    const baseMonthlyPayment = getBaseMonthlyPayment(state as any) ?? 0;
    const monthlyUseTax = getMonthlyUseTax(state as any) ?? 0;
    const implied = baseMonthlyPayment + monthlyUseTax;
    const variance = implied - stated;
    return {
        identity: 'lease.monthlyPayment',
        stated,
        implied,
        variance,
        inputs: { baseMonthlyPayment, monthlyUseTax },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseTotalMonthlyPayments = (state: StateTree): InvariantResult => {
    const stated = getBaseMonthlyPaymentTotal(state as any) ?? 0;
    const term = getSelectedTerm(state as any) as any;
    const months = term?.months ?? 1;
    const monthlyPayment = getMonthlyPayment(state) ?? 0;
    const implied = monthlyPayment * months;
    const variance = implied - stated;
    return {
        identity: 'lease.totalMonthlyPayments',
        stated,
        implied,
        variance,
        inputs: { monthlyPayment, months },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkLeaseTotalRentCharged = (state: StateTree): InvariantResult => {
    const term = getSelectedTerm(state as any) as any;
    const months = term?.months ?? 1;
    const monthlyRentCharged = getMonthlyRentCharged(state as any) ?? 0;
    const implied = monthlyRentCharged * months;
    const stated = term?.totalInterest;
    if (stated == null) {
        return {
            identity: 'lease.totalRentCharged',
            stated: 0,
            implied,
            variance: implied,
            inputs: { monthlyRentCharged, months },
            passed: false
        };
    }
    const variance = implied - stated;
    return {
        identity: 'lease.totalRentCharged',
        stated,
        implied,
        variance,
        inputs: { monthlyRentCharged, months },
        passed: Math.abs(variance) < TOLERANCE * months
    };
};

export const checkLeaseDasBreakdown = (state: StateTree): InvariantResult => {
    const stated = getDiaDueAtSigning(state as any) ?? 0;
    const paymentCapitalized =
        getFirstPaymentCapitalized(state as any) ?? calculationDetailsSelectors.getIsFirstPaymentCapitalized(state as any);
    const onePayLease = isOnePayLease(state as any);
    const onePayLeaseFirstPaymentEnabled = featureToggleSelectors.isOnePayLeaseFirstPaymentEnabled(state as any);
    const firstPmt = paymentCapitalized
        ? 0
        : onePayLeaseFirstPaymentEnabled && onePayLease
        ? getFirstPaymentAmount(state as any) ?? getBaseMonthlyPaymentTotal(state as any) ?? 0
        : getMonthlyPayment(state) ?? 0;
    const upfrontTaxes = getUpfrontTaxTotal(state as any) ?? 0;
    const lenderFees = getDiaLenderFees(state as any) ?? 0;
    const govFees = getDiaGovFees(state as any) ?? 0;
    const dealerFees = getDiaDealerFees(state as any) ?? 0;
    const protection = getDiaProtectionProducts(state as any) ?? 0;
    const accessories = getDiaAccessories(state as any) ?? 0;
    const secDeposit = getSecurityDepositOverrideWithFallback(state as any) ?? 0;
    const capCostReduction = getDasCapCostReduction(state) ?? 0;
    const implied =
        firstPmt + upfrontTaxes + lenderFees + govFees + dealerFees + protection + accessories + secDeposit + capCostReduction;
    const variance = implied - stated;
    return {
        identity: 'lease.dasBreakdown',
        stated,
        implied,
        variance,
        inputs: {
            firstPmt,
            onePayLease: onePayLease ? 1 : 0,
            upfrontTaxes,
            lenderFees,
            govFees,
            dealerFees,
            protection,
            accessories,
            secDeposit,
            capCostReduction
        },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const runLeaseInvariants = (state: StateTree): InvariantResult[] => {
    if (getCurrentOfferType(state as any) !== LEASE) return [];
    const toLeaseId = (r: InvariantResult): InvariantResult => ({
        ...r,
        identity: r.identity.replace(/^finance\./, 'lease.')
    });
    return [
        checkLeaseAdjustedCapCost(state),
        checkLeaseGrossCapCost(state),
        checkLeaseCapCostReduction(state),
        checkLeaseAddsToCapCost(state),
        checkLeaseTotalDepreciation(state),
        checkLeaseBaseMonthlyPayment(state),
        checkLeaseMonthlyPayment(state),
        checkLeaseTotalMonthlyPayments(state),
        checkLeaseTotalRentCharged(state),
        checkLeaseDasBreakdown(state),
        toLeaseId(checkProtectionTotal(state)),
        toLeaseId(checkDealerFeesTotal(state)),
        toLeaseId(checkGovFeesTotal(state)),
        toLeaseId(checkSellingPrice(state))
    ];
};

export const checkCashNetSellingPrice = (state: StateTree): InvariantResult => {
    const stated = getNetSellingPrice(state) ?? 0;
    const sellingPrice = getEffectiveSellingPrice(state, undefined) ?? 0;
    const totalAddons = getTotalAddons(state) ?? 0;
    const totalTax = getTotalTax(state as any) ?? 0;
    const implied = sellingPrice + totalAddons + totalTax;
    const variance = implied - stated;
    return {
        identity: 'cash.netSellingPrice',
        stated,
        implied,
        variance,
        inputs: { sellingPrice, totalAddons, totalTax },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const checkCashTotalAmountDue = (state: StateTree): InvariantResult => {
    const stated = getCashTotalAmountDueAfterCredits(state as any) ?? 0;
    const netSP = getNetSellingPrice(state) ?? 0;
    const netTrades = getTotalNetTradeValueForIncludedActiveTradeIns(state) ?? 0;
    const rebates = pickRebates(state);
    const cashDown = featureToggleSelectors.isCashDownPaymentFieldEnabled(state as any)
        ? selectEffectiveDownPayment(state, undefined) ?? 0
        : 0;
    const implied = netSP - netTrades - rebates - cashDown;
    const variance = implied - stated;
    return {
        identity: 'cash.totalAmountDue',
        stated,
        implied,
        variance,
        inputs: { netSellingPrice: netSP, netTrades, rebates, cashDown },
        passed: Math.abs(variance) < TOLERANCE
    };
};

export const runCashInvariants = (state: StateTree): InvariantResult[] => {
    if (getCurrentOfferType(state as any) !== CASH) return [];
    const toCashId = (r: InvariantResult): InvariantResult => ({
        ...r,
        identity: r.identity.replace(/^finance\./, 'cash.')
    });
    return [
        checkCashTotalAmountDue(state),
        checkCashNetSellingPrice(state),
        toCashId(checkTotalTax(state)),
        toCashId(checkPrimaryTaxTotal(state)),
        toCashId(checkAdditionalTaxTotal(state)),
        toCashId(checkSellingPrice(state)),
        toCashId(checkLenderFeesExcludedFromAddons(state)),
        toCashId(checkProtectionTotal(state)),
        toCashId(checkDealerFeesTotal(state)),
        toCashId(checkGovFeesTotal(state))
    ];
};

export const isMathStackEvaluationActive = (state: StateTree): boolean =>
    featureToggleSelectors.areFeatureTogglesAvailable(state as any) &&
    featureToggleSelectors.isMathStackEvaluationEnabled(state as any);

// Shared empty result so "nothing to evaluate" is always the same reference for useSelector.
const NO_RESULTS: InvariantResult[] = [];

// Cheapest exits first: a payment in flight has stale totals that would show as false variances, and
// offer types other than FINANCE, LEASE and CASH have no checks.
const evaluateInvariants = (state: StateTree): InvariantResult[] => {
    if (isCalculatingPayment(state as any)) return NO_RESULTS;
    switch (getCurrentOfferType(state as any)) {
        case FINANCE:
            return runFinanceInvariants(state);
        case LEASE:
            return runLeaseInvariants(state);
        case CASH:
            return runCashInvariants(state);
        default:
            return NO_RESULTS;
    }
};

// createSelector replaces module-level caches, which were shared across stores and tests and kept an
// old state tree alive. isEqual keeps the same array reference when a new state yields equal results,
// so a component subscribed through useSelector does not re-render on every dispatch.
const selectInvariantResults = createSelector([(state: StateTree) => state], evaluateInvariants, {
    memoizeOptions: { resultEqualityCheck: isEqual }
});

// Single owner of the toggle gate: consumers read variances from here and do not check the toggle themselves.
export const runMathStackInvariants = (state: StateTree): InvariantResult[] =>
    isMathStackEvaluationActive(state) ? selectInvariantResults(state) : NO_RESULTS;

const selectVariances = createSelector(
    [(state: StateTree) => runMathStackInvariants(state)],
    (results): InvariantResult[] => results.filter((r) => !r.passed),
    { memoizeOptions: { resultEqualityCheck: isEqual } }
);

export const getMathStackVariances = (state: StateTree): InvariantResult[] => selectVariances(state);
